import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Users, 
  Target, 
  TrendingUp, 
  Award,
  Brain,
  UserCheck,
  ClipboardCheck,
  MessageSquare,
  Briefcase,
  Lightbulb,
  HeartHandshake,
  BarChart3,
  Shield,
  Compass,
  CheckCircle
} from "lucide-react";

const ServicosPage = () => {
  const macroAreas = [
    {
      id: "recrutamento",
      icon: Users,
      title: "Recrutamento & Avaliação",
      description: "Seleção estratégica e avaliação profunda para decisões assertivas sobre pessoas.",
      color: "bg-brand-terracota",
      services: [
        {
          icon: UserCheck,
          title: "Recrutamento e Seleção Estratégica",
          description: "Processo completo de seleção que vai além do currículo.",
          solves: "Contratações desalinhadas, alto turnover e perda de tempo com candidatos inadequados.",
          benefits: ["Redução de turnover", "Contratações mais assertivas", "Alinhamento cultural", "Economia de tempo e recursos"],
          delivery: "Mapeamento de perfil, entrevistas por competências, testes psicológicos e comportamentais, avaliação de fit cultural e parecer técnico completo."
        },
        {
          icon: Brain,
          title: "Avaliação Psicológica Organizacional",
          description: "Análise profunda de competências, personalidade e potencial.",
          solves: "Incerteza em promoções, dificuldade em identificar talentos e decisões baseadas apenas em intuição.",
          benefits: ["Decisões fundamentadas", "Identificação de potencial", "Mapeamento de desenvolvimento", "Redução de riscos"],
          delivery: "Identificação de habilidades, traços de personalidade, potencial de liderança, pontos de desenvolvimento e relatório para tomada de decisão."
        }
      ]
    },
    {
      id: "lideranca",
      icon: Target,
      title: "Liderança & Treinamentos",
      description: "Desenvolvimento de líderes e capacitação de equipes para alta performance.",
      color: "bg-brand-verde",
      services: [
        {
          icon: Compass,
          title: "Desenvolvimento de Liderança",
          description: "Formação completa para líderes que precisam evoluir.",
          solves: "Líderes despreparados, comunicação ineficaz, dificuldade em engajar equipes.",
          benefits: ["Liderança mais consciente", "Melhor comunicação", "Equipes mais engajadas", "Decisões mais assertivas"],
          delivery: "Desenvolvimento de inteligência emocional, comunicação assertiva, gestão de conflitos, tomada de decisão e liderança de equipes."
        },
        {
          icon: Briefcase,
          title: "Treinamentos Corporativos",
          description: "Capacitações práticas e aplicáveis à realidade da empresa.",
          solves: "Baixa produtividade, conflitos internos, falta de engajamento.",
          benefits: ["Equipes mais alinhadas", "Aumento de produtividade", "Cultura fortalecida", "Colaboradores mais motivados"],
          delivery: "Treinamentos em trabalho em equipe, produtividade, cultura organizacional, motivação, engajamento e gestão do tempo."
        }
      ]
    },
    {
      id: "consultoria",
      icon: TrendingUp,
      title: "Consultoria em Gestão de Pessoas",
      description: "Estruturação completa de processos e cultura organizacional.",
      color: "bg-brand-terra-humana",
      services: [
        {
          icon: ClipboardCheck,
          title: "Consultoria em Gestão de Pessoas",
          description: "Estruturação completa da área de pessoas.",
          solves: "Falta de processos definidos, cultura desorganizada, ausência de políticas claras.",
          benefits: ["Processos estruturados", "Cultura definida", "Políticas claras", "Crescimento organizado"],
          delivery: "Criação de processos internos, estruturação de cultura, definição de valores e propósito, políticas de gestão e planos de carreira."
        },
        {
          icon: BarChart3,
          title: "Clima Organizacional e Engajamento",
          description: "Diagnóstico e intervenção no clima da empresa.",
          solves: "Ambiente pesado, baixo engajamento, conflitos frequentes.",
          benefits: ["Ambiente mais saudável", "Maior engajamento", "Retenção de talentos", "Produtividade elevada"],
          delivery: "Pesquisa de clima, diagnóstico de equipe, plano de ação estratégico e acompanhamento de melhorias."
        },
        {
          icon: HeartHandshake,
          title: "Gestão de Conflitos",
          description: "Mediação e construção de relações mais saudáveis.",
          solves: "Conflitos recorrentes, comunicação quebrada, ambiente hostil.",
          benefits: ["Relações restauradas", "Comunicação efetiva", "Ambiente harmonioso", "Produtividade mantida"],
          delivery: "Mediação de conflitos internos e construção de relações mais saudáveis no ambiente corporativo."
        }
      ]
    },
    {
      id: "desenvolvimento",
      icon: Award,
      title: "Desenvolvimento de Equipes & Empreendedores",
      description: "Programas personalizados para crescimento individual e coletivo.",
      color: "bg-brand-neutro",
      services: [
        {
          icon: Lightbulb,
          title: "Mentoria para Empreendedores",
          description: "Acompanhamento estratégico para quem lidera o próprio negócio.",
          solves: "Solidão do empreendedor, decisões impulsivas, desequilíbrio vida-trabalho.",
          benefits: ["Clareza nas decisões", "Equilíbrio pessoal", "Crescimento sustentável", "Mentalidade fortalecida"],
          delivery: "Mentalidade empreendedora, autogestão emocional, tomada de decisões estratégicas, equilíbrio vida-trabalho."
        },
        {
          icon: MessageSquare,
          title: "Desenvolvimento Pessoal e Profissional",
          description: "Acompanhamento individual para profissionais em crescimento.",
          solves: "Falta de direção, ansiedade no trabalho, baixa autoconfiança.",
          benefits: ["Autoconhecimento", "Controle emocional", "Direcionamento de carreira", "Maior autoconfiança"],
          delivery: "Autoconhecimento, controle emocional, gestão de ansiedade, autoconfiança e direcionamento de carreira."
        },
        {
          icon: Shield,
          title: "Programas de Desenvolvimento Organizacional",
          description: "Projetos sob medida para transformação completa.",
          solves: "Necessidades específicas que exigem abordagem personalizada.",
          benefits: ["Solução personalizada", "Resultados sob medida", "Transformação profunda", "Alinhamento total"],
          delivery: "Projetos personalizados para alinhar desempenho, bem-estar e resultados conforme necessidade específica."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="servicos-page">
      {/* Hero Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-brand-base-clara via-[#E5DBC5] to-brand-surface relative overflow-hidden" data-testid="servicos-hero">
        {/* Decorative lines */}
        <div className="absolute right-12 lg:right-24 top-1/4 bottom-1/4 flex gap-3">
          <div className="w-[2px] h-full bg-brand-terra-humana/20" />
          <div className="w-[2px] h-full bg-brand-terracota/30 mt-8" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-sans text-sm tracking-[0.15em] text-brand-muted mb-6 flex items-center gap-3">
              <span className="w-6 h-[1px] bg-brand-muted" />
              NOSSOS SERVIÇOS
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-normal text-brand-neutro leading-[1.1] mb-6">
              Soluções estratégicas para cada{" "}
              <span className="italic text-brand-terracota">desafio organizacional.</span>
            </h1>
            <p className="font-sans text-lg lg:text-xl text-brand-muted leading-relaxed">
              Não basta contratar. É preciso alinhar, desenvolver e sustentar. Conheça todos os nossos serviços.
            </p>
          </div>
        </div>
      </section>

      {/* Services by Macro Area */}
      {macroAreas.map((area, areaIndex) => (
        <section 
          key={area.id}
          id={area.id}
          className={`py-16 lg:py-24 ${areaIndex % 2 === 0 ? 'bg-brand-surface' : 'bg-brand-base-clara'}`}
          data-testid={`servicos-${area.id}`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            {/* Area Header */}
            <div className="flex items-start gap-6 mb-12 lg:mb-16">
              <div className={`w-14 h-14 rounded-2xl ${area.color} flex items-center justify-center flex-shrink-0`}>
                <area.icon className="text-brand-base-clara" size={28} strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="font-serif text-2xl lg:text-3xl font-medium text-brand-neutro mb-2">
                  {area.title}
                </h2>
                <p className="font-sans text-brand-muted">
                  {area.description}
                </p>
              </div>
            </div>

            {/* Services Grid */}
            <div className="grid lg:grid-cols-2 gap-8">
              {area.services.map((service, serviceIndex) => (
                <div 
                  key={serviceIndex}
                  className="bg-brand-base-clara p-8 lg:p-10 rounded-2xl border border-brand-border"
                  data-testid={`service-${area.id}-${serviceIndex}`}
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-brand-terra-humana/10 flex items-center justify-center flex-shrink-0">
                      <service.icon className="text-brand-terracota" size={24} strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-serif text-xl font-medium text-brand-neutro mb-1">
                        {service.title}
                      </h3>
                      <p className="font-sans text-sm text-brand-muted">
                        {service.description}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {/* Problema que resolve */}
                    <div>
                      <p className="font-sans text-xs uppercase tracking-wider text-brand-terracota mb-2">
                        Problema que resolve
                      </p>
                      <p className="font-sans text-sm text-brand-neutro leading-relaxed">
                        {service.solves}
                      </p>
                    </div>

                    {/* Benefícios */}
                    <div>
                      <p className="font-sans text-xs uppercase tracking-wider text-brand-terracota mb-2">
                        Benefícios
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {service.benefits.map((benefit, i) => (
                          <span 
                            key={i}
                            className="inline-flex items-center gap-1 bg-brand-verde/10 text-brand-verde px-3 py-1 rounded-full font-sans text-xs"
                          >
                            <CheckCircle size={12} />
                            {benefit}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Entrega */}
                    <div>
                      <p className="font-sans text-xs uppercase tracking-wider text-brand-terracota mb-2">
                        O que entregamos
                      </p>
                      <p className="font-sans text-sm text-brand-muted leading-relaxed">
                        {service.delivery}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-brand-terracota" data-testid="servicos-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl lg:text-4xl font-medium text-brand-base-clara mb-6">
            Não sabe por onde começar?
          </h2>
          <p className="font-sans text-lg text-brand-base-clara/80 leading-relaxed mb-10 max-w-2xl mx-auto">
            Agende um diagnóstico estratégico gratuito. Vamos entender suas necessidades e indicar o melhor caminho para sua empresa.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/5586998325746?text=Olá! Gostaria de entender melhor qual serviço é ideal para minha empresa."
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-2 bg-brand-base-clara text-brand-neutro hover:bg-white px-8 py-4 rounded-full font-sans font-medium transition-premium"
              data-testid="servicos-cta-primary"
            >
              Agendar Diagnóstico Gratuito
              <ArrowRight size={18} />
            </a>
            <Link
              to="/planos"
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-brand-base-clara/50 text-brand-base-clara hover:bg-brand-base-clara/10 px-8 py-4 rounded-full font-sans font-medium transition-premium"
              data-testid="servicos-cta-secondary"
            >
              Ver Planos
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicosPage;
